import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

const SLIDER_RADIUS = 16;

const NumberBar = ({
  value,
  onChange,
  onValueUpdate,
  disabled = false,
  min = 0,
  max = 100,
  suffix = "%",
  sliderProps = {},
}) => {
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const newValueRef = useRef(value);
  const touchStartRef = useRef(null);
  const isScrolling = useRef(false);

  // Use localValue during dragging for immediate visual feedback
  const displayValue = dragging.current ? localValue : value;
  const percent = Math.max(0, Math.min(1, (displayValue - min) / (max - min)));

  // Sync localValue with prop value when not dragging
  useEffect(() => {
    if (!dragging.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(rect.width, x));
    const newPercent = x / rect.width;
    let newValue = Math.round(newPercent * (max - min) + min);
    newValue = Math.max(min, Math.min(max, newValue)); // Clamp value
    setLocalValue(newValue); // Update local state immediately for visual feedback
    if (onChange) onChange(newValue);
    newValueRef.current = newValue;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    dragging.current = true;
    setShowTooltip(true);
    handleMove(e.clientX);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setShowTooltip(false);
    if (onValueUpdate) onValueUpdate(newValueRef.current);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    if (disabled) return;
    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    if (isScrolling.current || !touchStartRef.current) return;

    if (dragging.current) {
      e.preventDefault();
      handleMove(e.touches[0].clientX);
      return;
    }

    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    const moveThreshold = 5;

    if (deltaX > moveThreshold || deltaY > moveThreshold) {
      if (deltaY > deltaX) {
        isScrolling.current = true;
        touchStartRef.current = null;
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      } else {
        e.preventDefault();
        dragging.current = true;
        setShowTooltip(true);
        handleMove(e.touches[0].clientX);
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isScrolling.current) {
      isScrolling.current = false;
      touchStartRef.current = null;
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      return;
    }

    if (dragging.current) {
      setShowTooltip(false);
      if (onValueUpdate) onValueUpdate(newValueRef.current);
    } else {
      // Tap event
      handleMove(e.changedTouches[0].clientX);
      if (onValueUpdate) onValueUpdate(newValueRef.current);
    }

    dragging.current = false;
    isScrolling.current = false;
    touchStartRef.current = null;
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  // Calculate absolute position for Portal tooltip
  const getTooltipPosition = () => {
    if (!containerRef.current) return { left: 0, top: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const thumbLeftOffset = rect.width * percent;
    return {
      left: rect.left + thumbLeftOffset,
      top: rect.top - 35,
    };
  };

  const tooltipPosition = getTooltipPosition();

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={cn(
        "relative w-full overflow-visible select-none",
        disabled ? "cursor-default opacity-80" : "cursor-pointer"
      )}
      style={{
        height: "40%",
        background: "hsl(var(--muted))",
        borderRadius: `${SLIDER_RADIUS}px`,
      }}
    >
      {showTooltip &&
        createPortal(
          <div
            className="fixed -translate-x-1/2 bg-white rounded-lg px-2 py-1 text-black text-sm font-bold z-[8000] pointer-events-none shadow-lg"
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
            }}
          >
            {Math.round(displayValue)}
            {suffix}
          </div>,
          document.body
        )}

      {/* Colored track */}
      <div
        className="absolute left-0 top-0 h-full z-[1] pointer-events-none"
        style={{
          width: `${percent * 100}%`,
          background: "hsl(var(--primary))",
          borderRadius: `${SLIDER_RADIUS}px`,
          transition: dragging.current ? "none" : "width 0.2s",
        }}
      />
    </div>
  );
};

export default NumberBar;
